import axios from 'axios';

const state = () => ({
  registrationLink: [],
  scheduleCallout: [],
  announcements: {},
  quotes: [],
  sessionMaterials: [],
  loading: false,
  audioFlag: false,
  mementoEnable: false,
  video: {},
  gicScheduleImage: '',
  gicPixiesetUrl: '',
  moreSections: [],
});

const getters = {
  getAnnouncements: state => conferenceType => {
    return state.announcements[conferenceType] || [];
  },
};

const mutations = {
  setAnnouncements(state, { conferenceType, announcements }) {
    state.announcements = { ...state.announcements, [conferenceType]: announcements };
  },
  setRegistrationLink(state, registrationLink) {
    state.registrationLink = registrationLink;
  },
  setScheduleCallout(state, scheduleCallout) {
    state.scheduleCallout = scheduleCallout;
  },
  setQuotes(state, quotes) {
    state.quotes = quotes;
  },
  setSessionMaterials(state, sessionMaterials) {
    state.sessionMaterials = sessionMaterials;
  },
  setGensmvmtMaterials(state, gensmvmtMaterials) {
    state.gensmvmtMaterials = gensmvmtMaterials;
  },
  setGicScheduleImage(state, gicScheduleImage) {
    state.gicScheduleImage = gicScheduleImage;
  },
  setGicPixiesetUrl(state, gicPixiesetUrl) {
    state.gicPixiesetUrl = gicPixiesetUrl;
  },
  setMoreSections(state, moreSections) {
    // Normalize so the page's v-for never iterates null/undefined
    state.moreSections = Array.isArray(moreSections) ? moreSections : [];
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setAudioFlag(state, audioFlag) {
    state.audioFlag = audioFlag;
  },
  setMementoEnable(state, mementoEnable) {
    state.mementoEnable = mementoEnable;
  },
  updateVideo(state, video) {
    state.video = video;
  },
};

const actions = {
  setLoading({ commit }, loading) {
    commit('setLoading', loading);
  },

  async fetchGicPhotodropForEveryone({ commit, dispatch, rootState }) {
    try {
      const accessToken = rootState.user.token;
      const response = await axios.get(
        process.env.VUE_APP_API_URL + '/cms/gic-photodrop-for-everyone',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      commit('setGicPixiesetUrl', response.data.gic2_photos_url);
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch GIC photodrop data.', { root: true });
    }
  },
  async fetchGicScheduleImage({ commit, dispatch, rootState }) {
    try {
      const accessToken = rootState.user.token;
      const response = await axios.get(process.env.VUE_APP_API_URL + '/cms/gic/schedule-image', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const imageUrl = response.data.url;
      commit('setGicScheduleImage', imageUrl);
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch GIC schedule image.', { root: true });
    }
  },
  async fetchAnnouncements(
    { commit, dispatch, rootState, state },
    { locale, conferenceType, force = false } = {},
  ) {
    const hasCached = !!state.announcements[conferenceType]?.length;
    if (!force && hasCached) return;
    // Stale-while-revalidate: only show the global loading state on first load.
    // Forced refreshes with cached data update silently, so returning to the
    // home page doesn't flash the full-page spinner.
    if (!hasCached) commit('setLoading', true);
    try {
      const accessToken = rootState.user.token;
      const params = {};
      if (locale) params.locale = locale;
      if (conferenceType) params.conferenceType = conferenceType;
      const { data: announcements } = await axios.get(
        process.env.VUE_APP_API_URL + '/cms/announcements/get',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params,
        },
      );
      const sorted = Array.isArray(announcements)
        ? announcements.sort((a, b) => new Date(b.postedTime) - new Date(a.postedTime))
        : [];
      commit('setAnnouncements', { conferenceType, announcements: sorted });
    } catch (e) {
      console.log(e);
      dispatch('toast/error', 'Failed to fetch announcements.', { root: true });
    } finally {
      if (!hasCached) commit('setLoading', false);
    }
  },
  async fetchRegistrationLink(
    { commit, dispatch, rootState },
    { locale = 'en', conferenceType = 'Global' },
  ) {
    try {
      const accessToken = rootState.user.token;
      const { data: registrationLink } = await axios.get(
        process.env.VUE_APP_API_URL + '/cms/registration-links',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            locale: locale,
            conferenceType: conferenceType,
          },
        },
      );
      commit('setRegistrationLink', registrationLink[0]);
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch registration link.', {
        root: true,
      });
    }
  },
  async fetchQuotes({ commit, dispatch, rootState, state }, resourceName) {
    if (state.quotes.length) return;
    commit('setLoading', true);
    try {
      const accessToken = rootState.user.token;
      const { data: quotes } = await axios.get(
        process.env.VUE_APP_API_URL + '/cms/' + resourceName,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      commit('setQuotes', quotes);
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch quotes.', { root: true });
    } finally {
      commit('setLoading', false);
    }
  },
  async fetchSessionMaterials(
    { commit, dispatch, rootState, state },
    { sessionId, conferenceType },
  ) {
    if (state.sessionMaterials.length) return;
    commit('setLoading', true);
    try {
      const accessToken = rootState.user.token;
      const { data: sessionMaterials } = await axios.get(
        process.env.VUE_APP_API_URL + '/cms/sessionMaterials/' + sessionId,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            conferenceType: conferenceType,
          },
        },
      );
      commit('setSessionMaterials', sessionMaterials[0]);
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch session materials.', {
        root: true,
      });
    } finally {
      commit('setLoading', false);
    }
  },
  async fetchVideoBySlug({ commit, rootState, state }, { slug }) {
    if (state.video?.slug) return;
    commit('setLoading', true);
    try {
      const accessToken = rootState.user.token;
      // TODO to remove after we merge the collection
      let response = null;
      response = await axios.get(process.env.VUE_APP_API_URL + `/cms/videos/${slug}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      commit('updateVideo', response.data);
      commit('setLoading', false);
    } catch (error) {
      commit('setLoading', false);
    }
  },
  async fetchAllSessionMaterials({ commit, dispatch, rootState, state }) {
    if (state.sessionMaterials.length) return;
    commit('setLoading', true);
    try {
      const accessToken = rootState.user.token;
      const { data: sessionMaterials } = await axios.get(
        process.env.VUE_APP_API_URL + '/cms/sessionMaterials/all',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      commit('setSessionMaterials', sessionMaterials);
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch session materials.', {
        root: true,
      });
    } finally {
      commit('setLoading', false);
    }
  },
  async fetchAudioFlag({ commit, dispatch }) {
    try {
      // v2: read AUDIO_FLAG from the single shared sysparams payload (cached).
      const data = await dispatch('sysparams/fetchParams', undefined, { root: true });
      commit('setAudioFlag', data.AUDIO_FLAG);
    } catch (e) {
      dispatch('toast/error', 'Failed to load profile.', { root: true });
    } finally {
      commit('setLoading', false);
    }
  },
  async fetchMementoEnable({ commit, dispatch, rootState, rootGetters }) {
    try {
      const accessToken = rootState.user.token;
      const paramName = 'MEMENTO_ENABLE';
      // Testers get the preview value via ?preview=1 (app-enforced).
      const isTester = rootGetters?.['profile/isTester'] === true;
      const { status, data } = await axios.get(
        process.env.VUE_APP_API_URL +
          '/sysparams/params/' +
          paramName +
          (isTester ? '?preview=1' : ''),
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (status !== 200) throw new Error('Failed in getting memento enable flag');
      commit('setMementoEnable', data);
    } catch (e) {
      dispatch('toast/error', 'Failed to load photos.', { root: true });
    } finally {
      commit('setLoading', false);
    }
  },
  setResources(state, resources) {
    if (!Array.isArray(resources)) state.resources = [resources];
    else state.resources = resources;
  },
  async fetchMoreSections({ commit, dispatch, rootState }) {
    try {
      const accessToken = rootState.user.token;
      const { data: moreSections } = await axios.get(
        process.env.VUE_APP_API_URL + '/cms/more-sections',
        // v=2 opts into card sections; without it the API omits card-only
        // sections so legacy builds don't render empty list boxes.
        { headers: { Authorization: `Bearer ${accessToken}` }, params: { v: 2 } },
      );
      commit('setMoreSections', moreSections);
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch more sections.', { root: true });
    }
  },

  async fetchScheduleCallout({ commit, dispatch, rootState }, { _locale } = {}) {
    try {
      const accessToken = rootState.user.token;
      const { data: scheduleCallout } = await axios.get(
        process.env.VUE_APP_API_URL + '/cms/gc-app-schedule-callouts',
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      commit('setScheduleCallout', scheduleCallout);
    } catch (e) {
      dispatch('toast/error', 'Failed to fetch schedule callout.', {
        root: true,
      });
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
